import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';

export interface BranchServiceStatusShippingConfigItem {
  statusId: string;
  statusCode?: string;
  statusName?: string;
  enabled: boolean;
  shippingMethodIds: string[];
}

export interface BranchServiceShippingConfig {
  branchId: string;
  serviceId: string;
  shippingMethodIds: string[];
  statuses: BranchServiceStatusShippingConfigItem[];
}

export interface BranchServiceShippingConfigUpsertPayload {
  shippingMethodIds: string[];
  statuses: Array<{
    statusId: string;
    enabled: boolean;
    shippingMethodIds: string[];
  }>;
}

/**
 * Drawer «Configurar servicio» (sucursal): formas del servicio + estatus×formas.
 *
 * GET/PUT `/api/structure/branches/{branchId}/services/{serviceId}/shipping-config`
 */
@Injectable({ providedIn: 'root' })
export class BranchServiceShippingConfigService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private url(branchId: string, serviceId: string): string {
    return `${this.apiConfig.apiUrl}/structure/branches/${branchId}/services/${serviceId}/shipping-config`;
  }

  get(
    branchId: string,
    serviceId: string,
  ): Observable<BranchServiceShippingConfig> {
    return this.httpClient
      .get<ApiResponse<BranchServiceShippingConfig>>(
        this.url(branchId, serviceId),
      )
      .pipe(map((response) => this.normalize(response.data, branchId, serviceId)));
  }

  update(
    branchId: string,
    serviceId: string,
    payload: BranchServiceShippingConfigUpsertPayload,
  ): Observable<BranchServiceShippingConfig> {
    return this.httpClient
      .put<ApiResponse<BranchServiceShippingConfig>>(
        this.url(branchId, serviceId),
        {
          shippingMethodIds: payload.shippingMethodIds ?? [],
          statuses: payload.statuses ?? [],
        },
      )
      .pipe(
        map((response) =>
          this.normalize(response.data, branchId, serviceId),
        ),
      );
  }

  private normalize(
    data: BranchServiceShippingConfig | undefined,
    branchId: string,
    serviceId: string,
  ): BranchServiceShippingConfig {
    if (!data) {
      return {
        branchId,
        serviceId,
        shippingMethodIds: [],
        statuses: [],
      };
    }
    return {
      branchId: data.branchId || branchId,
      serviceId: data.serviceId || serviceId,
      shippingMethodIds: Array.isArray(data.shippingMethodIds)
        ? data.shippingMethodIds.map(String)
        : [],
      statuses: Array.isArray(data.statuses)
        ? data.statuses.map((s) => ({
            statusId: String(s.statusId),
            statusCode: s.statusCode,
            statusName: s.statusName,
            enabled: !!s.enabled,
            shippingMethodIds: Array.isArray(s.shippingMethodIds)
              ? s.shippingMethodIds.map(String)
              : [],
          }))
        : [],
    };
  }
}
